import {
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Tab,
  Tabs,
  TextField,
  Typography
} from '@material-ui/core'
import Autocomplete from '@material-ui/lab/Autocomplete'
import { isEmpty } from 'lodash'
import React from 'react'
import { useLocation } from 'react-router-dom'

import {
  getAnalyticsDashboardData,
  getAnalyticsFilterOptions,
  listAnalyticsDashboards
} from 'api/analytics'
import { PageTabsBar } from 'app/components/generic/PageLayout'
import PreschoolQuality from 'app/pages/Operations/CentreOpsReport/PreschoolQuality'

import BarChart from './BarChart'
import LineChart from './LineChart'
import useStyles from './styles'

const PREVIEW_ROWS = 6
const MAX_CONCURRENT = 3

const FILTER_LABELS = {
  centre: 'Centre',
  subject: 'Subject',
  start_date: 'Start date',
  end_date: 'End date',
  age: 'Age',
  teacher_phone_number: 'Teacher phone',
  cancellation_reason: 'Cancellation Reason',
  at_home_apartment: 'Apartment',
  search: 'Search'
}

// Tabs that are a full page of their own, not SQL cards: shown after the
// group's dashboard tabs, with no filter bar (the page has its own controls).
const PAGE_TABS = {
  // Preschool Quality & Ops (was Preschool Ops — Satya, 28 Oct) = the
  // Preschool Ops overview + the Teacher Scorecard (moved from Centre Audit),
  // one card each. Checklist, interactions and parent work have their own HQ
  // pages.
  centre_health_v2: [
    // Renamed (Satya, 28 Oct); old ?tab= links still land here (aliases).
    {
      label: 'Quality and Ops',
      aliases: ['Preschool Quality & Ops'],
      Component: PreschoolQuality
    }
  ]
}

const groupKeyFromPath = (pathname) => {
  const seg = (pathname || '').split('/').filter(Boolean).pop()
  return seg === 'analytics' ? '' : seg
}

const formatValue = (v) => {
  if (v === null || v === undefined) return '-'
  if (typeof v === 'object') return v.toString?.() || JSON.stringify(v)
  return String(v)
}

// Parent-name cells link to the Parent dashboard (new tab). The legacy
// "Alfred Link" column is hidden — its parent id is reused for the link.
const ALFRED_RE = /alfred.?link/i
const PARENT_NAME_RE = /parent.?name/i
// The parent's phone opens the same profile (owner, 2026-10-23).
const PARENT_PHONE_RE = /^parent'?s?.?phone/i
const PARENT_ID_KEYS = [
  'parent_id',
  'parent_uuid',
  'parent_phone_number',
  'parent_phone'
]

// Raw id columns (parent_id / student_id) are carried in list queries for the
// parent link + joins, but are noise on screen — hide them like the Alfred
// Link column. The parent link still resolves from the full (unfiltered) row.
const HIDDEN_ID_RE = /^(parent|student|user|child)_id$/i
// Internal ordering columns some queries carry (to sort a Total row last) —
// never meant to be displayed.
const HIDDEN_META_RE = /^(sort\s*flag|rank)$/i
const visibleColumns = (columns) =>
  columns.filter(
    (c) =>
      !ALFRED_RE.test(c) && !HIDDEN_ID_RE.test(c) && !HIDDEN_META_RE.test(c)
  )

const parentRef = (row, columns) => {
  for (const k of PARENT_ID_KEYS) {
    const col = columns.find((c) => c.toLowerCase() === k)
    if (col && row[col]) return String(row[col])
  }
  const alfredCol = columns.find((c) => ALFRED_RE.test(c))
  if (alfredCol && row[alfredCol]) {
    const m = String(row[alfredCol]).match(/parent=([^&\s]+)/)
    if (m) return decodeURIComponent(m[1])
  }
  return null
}

const renderCell = (row, column, columns) => {
  const value = formatValue(row[column])
  if (
    (PARENT_NAME_RE.test(column) || PARENT_PHONE_RE.test(column)) &&
    row[column]
  ) {
    const ref = parentRef(row, columns)
    if (ref) {
      return (
        <a
          href={`/home/parent?parent=${encodeURIComponent(ref)}`}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
        >
          {value}
        </a>
      )
    }
  }
  return value
}

const makeLimiter = (max) => {
  let active = 0
  const q = []
  const pump = () => {
    if (active >= max || !q.length) return
    active += 1
    const { fn, res, rej } = q.shift()
    fn()
      .then(res, rej)
      .finally(() => {
        active -= 1
        pump()
      })
  }
  return (fn) =>
    new Promise((res, rej) => {
      q.push({ fn, res, rej })
      pump()
    })
}

// Grid span per card: bar charts and `full` cards take the whole row, `wide`
// cards two-thirds, everything else a third — so wide tables aren't cramped.
const FULL_WIDTH_CHARTS = ['bar', 'line', 'hbar']

const gridSpan = (d) => {
  if (d.chart && FULL_WIDTH_CHARTS.includes(d.chart.type))
    return { sm: 12, lg: 12 }
  if (d.width === 'full') return { sm: 12, lg: 12 }
  if (d.width === 'wide') return { sm: 12, lg: 8 }
  return { sm: 6, lg: 4 }
}

// Colour horizontal-bar categories by sentiment (used by the consumption
// change breakdown). Unknown categories fall back to the theme blue.
const HBAR_COLORS = {
  New: '#66bb6a',
  Winback: '#66bb6a',
  Upsell: '#43a047',
  'Cross-sell': '#26a69a',
  'Pause Return': '#9ccc65',
  'No change': '#b0bec5',
  Paused: '#ffca28',
  Downsell: '#ffa726',
  Lost: '#ef5350'
}

// Dependency-free CSV export for the drill list.
const csvDownload = (columns, rows, filename) => {
  const esc = (v) => `"${String(v == null ? '' : v).replace(/"/g, '""')}"`
  const body = [
    columns.map(esc).join(','),
    ...rows.map((r) => columns.map((c) => esc(r[c])).join(','))
  ].join('\n')
  const url = URL.createObjectURL(
    new Blob([body], { type: 'text/csv;charset=utf-8;' })
  )
  const a = document.createElement('a')
  a.href = url
  a.download = filename || 'list.csv'
  a.click()
  URL.revokeObjectURL(url)
}

// Column width weight: names widest, category/program/centre/teacher a bit
// wider, everything else even. Normalised to 100% so the fixed-layout table
// fills the view exactly.
const drillWeight = (c) =>
  /name/i.test(c)
    ? 2
    : /category|program|batch|centre|teacher/i.test(c)
    ? 1.4
    : 1

// The drill list, rendered as a plain table we fully control (mui-datatables
// wouldn't let us wrap header labels or hold column widths). overflow-x:hidden
// on the scroll box + width:100% + table-layout:fixed make it impossible for
// the table to scroll sideways or leave dead space on the right; headers and
// long values wrap onto more lines (never cut off). Client-side search + CSV export retained.
// gridMode: columns size to their content and the table scrolls horizontally
// (spreadsheet-style, for the wide inline data tables). Default (drill modals)
// keeps the fit-to-width fixed layout so those never scroll sideways.
const DrillTable = ({
  rows,
  columns,
  allColumns,
  filename,
  classes,
  gridMode
}) => {
  const [q, setQ] = React.useState('')
  const query = q.trim().toLowerCase()
  const filtered = query
    ? rows.filter((r) =>
        columns.some((c) =>
          String(r[c] == null ? '' : r[c])
            .toLowerCase()
            .includes(query)
        )
      )
    : rows
  const weights = columns.map(drillWeight)
  const totalW = weights.reduce((a, b) => a + b, 0) || 1

  // Grid mode: draggable per-column widths (px). Default from the column
  // weight; a fixed table layout then lets columns truncate and be resized.
  const gridBaseW = (c) => Math.round(drillWeight(c) * 130)
  const [colW, setColW] = React.useState({})
  const startResize = (c) => (e) => {
    e.preventDefault()
    e.stopPropagation()
    const startX = e.clientX
    const startW = colW[c] || gridBaseW(c)
    const onMove = (ev) =>
      setColW((prev) => ({
        ...prev,
        [c]: Math.max(60, startW + (ev.clientX - startX))
      }))
    const onUp = () => {
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup', onUp)
    }
    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
  }
  const totalGridW = gridMode
    ? columns.reduce((s, c) => s + (colW[c] || gridBaseW(c)), 0)
    : 0

  return (
    <div className={classes.drillWrap}>
      <div className={classes.drillToolbar}>
        <input
          className={classes.drillSearch}
          placeholder="Search…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <button
          type="button"
          className={classes.drillDownload}
          onClick={() => csvDownload(columns, filtered, filename)}
        >
          ⬇ Download CSV
        </button>
      </div>
      <div className={gridMode ? classes.drillScrollGrid : classes.drillScroll}>
        <table
          className={gridMode ? classes.drillTableGrid : classes.drillTable}
          style={gridMode ? { width: totalGridW } : undefined}
        >
          <colgroup>
            {columns.map((c, i) => (
              <col
                key={c}
                style={{
                  width: gridMode
                    ? `${colW[c] || gridBaseW(c)}px`
                    : `${((weights[i] / totalW) * 100).toFixed(2)}%`
                }}
              />
            ))}
          </colgroup>
          <thead>
            <tr>
              {columns.map((c) => (
                <th
                  key={c}
                  className={gridMode ? classes.drillThGrid : classes.drillTh}
                >
                  {c}
                  {gridMode ? (
                    <span
                      className={classes.resizeHandle}
                      onMouseDown={startResize(c)}
                    />
                  ) : null}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map((r, ri) => (
              <tr key={ri} className={ri % 2 ? classes.drillTrAlt : undefined}>
                {columns.map((c) => (
                  <td
                    key={c}
                    className={
                      gridMode
                        ? classes.drillTd
                        : `${classes.drillTd} ${classes.drillTdWrap}`
                    }
                    title={formatValue(r[c])}
                  >
                    {renderCell(r, c, allColumns)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 ? (
          <div className={classes.drillEmpty}>No matching records.</div>
        ) : null}
      </div>
    </div>
  )
}

const Dashlet = ({ dashboard, params, limit, classes }) => {
  const [data, setData] = React.useState(null)
  const [error, setError] = React.useState(false)
  const [open, setOpen] = React.useState(false)
  // Drill-down state: `drill` holds the detail-list request once triggered.
  const [drill, setDrill] = React.useState(null)
  const [drillData, setDrillData] = React.useState(null)
  const [drillError, setDrillError] = React.useState(false)

  const drillCfg = dashboard.drill
  const chartCfg = dashboard.chart
  const isBar = chartCfg && chartCfg.type === 'bar'
  const isLine = chartCfg && chartCfg.type === 'line'
  const isHBar = chartCfg && chartCfg.type === 'hbar'
  const isRatio = chartCfg && chartCfg.type === 'ratio'
  const isPivot = chartCfg && chartCfg.type === 'pivot'
  // `type: table` renders the full data inline as the controlled DrillTable
  // (search + CSV + sticky header) instead of the small click-to-expand preview.
  const isTable = chartCfg && chartCfg.type === 'table'

  React.useEffect(() => {
    let mounted = true
    setData(null)
    setError(false)
    limit(() => getAnalyticsDashboardData(dashboard.id, params))
      .then((d) => mounted && setData(d))
      .catch(() => mounted && setError(true))
    return () => {
      mounted = false
    }
  }, [dashboard.id, params, limit])

  // Fetch the detail list when a drill is opened (KPI = whole list; a table
  // row = the list filtered by that row's dimension value).
  React.useEffect(() => {
    if (!drill) return undefined
    let mounted = true
    setDrillData(null)
    setDrillError(false)
    limit(() => getAnalyticsDashboardData(drillCfg.dashboard, drill.params))
      .then((d) => mounted && setDrillData(d))
      .catch(() => mounted && setDrillError(true))
    return () => {
      mounted = false
    }
  }, [drill, drillCfg, limit])

  const allColumns = data?.columns || []
  // A dashboard can pin an explicit display-column allowlist (in order);
  // otherwise fall back to hiding the internal id/link/meta columns.
  const columns =
    dashboard.columns && dashboard.columns.length
      ? dashboard.columns.filter((c) => allColumns.includes(c))
      : visibleColumns(allColumns)
  const rows = data?.rows || []
  // A KPI is a single value (one column). A multi-column result stays a
  // table even when a filter narrows it to one row — otherwise e.g. the
  // Toddler Club table collapses into a KPI when a centre has one program.
  const isKpi = data && rows.length === 1 && columns.length === 1

  // Drill can filter on the row's dimension (e.g. Category) and, when a
  // measure cell is clicked, add that column's filter (e.g. the M+1 count
  // drills to just the retained students via cellFilters). Called with
  // (null) from KPI/ratio cards for the whole list.
  const openDrill = (row, clickedCol) => {
    if (!drillCfg) return
    // `drill.params` are fixed filters for this card's list (e.g. the At Home
    // "Attended" card always opens the attended trials).
    const p = { ...params, ...(drillCfg.params || {}) }
    const chips = []
    if (drillCfg.column && drillCfg.param && row) {
      const v = row[drillCfg.column]
      if (v != null && v !== '') {
        p[drillCfg.param] = v
        chips.push(String(v))
      }
    }
    // `rowParams` ({param: column}) carry more of the row's dimensions, for a
    // table keyed on two columns (At Home inventory: apartment + category).
    if (drillCfg.rowParams && row) {
      Object.entries(drillCfg.rowParams).forEach(([param, col]) => {
        const v = row[col]
        if (v != null && v !== '') {
          p[param] = v
          chips.push(String(v))
        }
      })
    }
    const cf =
      drillCfg.cellFilters && clickedCol && drillCfg.cellFilters[clickedCol]
    if (cf && cf.param) {
      p[cf.param] = cf.value
      chips.push(clickedCol)
    }
    setDrill({
      title: dashboard.panel_title || dashboard.title,
      chips,
      params: p
    })
  }

  // Pivot cell drill: pass the row dimension (e.g. Centre) and, when a specific
  // cell is clicked, the column dimension (e.g. Subject) as extra params.
  const openDrillCell = (extra, chips) => {
    if (!drillCfg) return
    setDrill({
      title: dashboard.panel_title || dashboard.title,
      chips,
      params: { ...params, ...extra }
    })
  }

  const closeDialog = () => {
    setOpen(false)
    setDrill(null)
    setDrillData(null)
    setDrillError(false)
  }

  // The expand dialog shows the drilled detail list when drilling, else the
  // card's own rows (unchanged behaviour for cards without a drill config).
  const dialogOpen = open || !!drill
  const dialogTitle = drill
    ? drill.title
    : dashboard.panel_title || dashboard.title
  const dialogChips = (drill && drill.chips) || []
  const dialogData = drill ? drillData : data
  const dialogAllColumns = dialogData?.columns || []
  const dialogColumns = visibleColumns(dialogAllColumns)
  const dialogRows = dialogData?.rows || []

  return (
    <>
      <Paper
        className={
          isBar || isLine || isHBar || isPivot || isTable
            ? `${classes.dashlet} ${classes.chartDashlet}`
            : classes.dashlet
        }
        elevation={1}
        onClick={
          drillCfg
            ? isRatio
              ? () => openDrill(null)
              : undefined
            : isTable
            ? undefined
            : () => data && rows.length && setOpen(true)
        }
      >
        {isTable ? null : (
          <Typography className={classes.dashletTitle} variant="subtitle2">
            {dashboard.panel_title || dashboard.title}
          </Typography>
        )}
        <div className={classes.dashletBody}>
          {error ? (
            <Typography variant="body2" color="error">
              Could not load.
            </Typography>
          ) : !data ? (
            <div className={classes.dashletLoading}>
              <CircularProgress size={22} />
            </div>
          ) : rows.length === 0 ? (
            <Typography variant="body2" className={classes.muted}>
              No data.
            </Typography>
          ) : isBar ? (
            <BarChart rows={rows} xKey={chartCfg.x} yKey={chartCfg.y} />
          ) : isLine ? (
            <LineChart rows={rows} xKey={chartCfg.x} yKey={chartCfg.y} />
          ) : isHBar ? (
            (() => {
              const vals = rows.map((r) => Number(r[chartCfg.y]) || 0)
              const maxV = Math.max(1, ...vals)
              return (
                <div className={classes.hbarWrap}>
                  {rows.map((r, i) => {
                    const label = formatValue(r[chartCfg.x])
                    const val = vals[i]
                    return (
                      <div
                        key={i}
                        className={
                          drillCfg ? classes.hbarRowClickable : classes.hbarRow
                        }
                        onClick={
                          drillCfg
                            ? (e) => {
                                e.stopPropagation()
                                openDrill(r, null)
                              }
                            : undefined
                        }
                      >
                        <div className={classes.hbarLabel}>{label}</div>
                        <div className={classes.hbarTrack}>
                          <div
                            className={classes.hbarFill}
                            style={{
                              width: `${(val / maxV) * 100}%`,
                              backgroundColor: HBAR_COLORS[label] || '#5b9bd5'
                            }}
                          />
                        </div>
                        <div className={classes.hbarVal}>
                          {val.toLocaleString()}
                        </div>
                      </div>
                    )
                  })}
                </div>
              )
            })()
          ) : isRatio ? (
            (() => {
              const done = Number(rows[0][chartCfg.done]) || 0
              const total = Number(rows[0][chartCfg.total]) || 0
              const pct = total > 0 ? (done / total) * 100 : 0
              return (
                <div className={classes.ratioWrap}>
                  <div className={classes.ratioValue}>
                    {done.toLocaleString()}
                    <span className={classes.ratioTotal}>
                      {' / '}
                      {total.toLocaleString()}
                    </span>
                  </div>
                  <div className={classes.ratioBarOuter}>
                    <div
                      className={classes.ratioBarInner}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <div className={classes.ratioCaption}>
                    {Math.round(pct * 10) / 10}% {chartCfg.label || 'done'}
                  </div>
                </div>
              )
            })()
          ) : isKpi ? (
            <div className={classes.kpiRow}>
              {columns.map((c) => (
                <div
                  key={c}
                  className={classes.kpi}
                  onClick={
                    drillCfg
                      ? (e) => {
                          e.stopPropagation()
                          openDrill(null)
                        }
                      : undefined
                  }
                  style={drillCfg ? { cursor: 'pointer' } : undefined}
                >
                  <div className={classes.kpiValue}>
                    {formatValue(rows[0][c])}
                  </div>
                  <div className={classes.kpiLabel}>{c}</div>
                </div>
              ))}
            </div>
          ) : isPivot ? (
            (() => {
              const rowKey = chartCfg.row
              const colKey = chartCfg.col
              const valKey = chartCfg.value
              const rowKeys = Array.from(
                new Set(rows.map((r) => r[rowKey]))
              ).sort()
              const colKeys = Array.from(
                new Set(rows.map((r) => r[colKey]))
              ).sort()
              const cellMap = {}
              rows.forEach((r) => {
                cellMap[`${r[rowKey]}||${r[colKey]}`] = Number(r[valKey]) || 0
              })
              const maxCell = Math.max(1, ...Object.values(cellMap))
              const rowTotal = (rk) =>
                colKeys.reduce((s, ck) => s + (cellMap[`${rk}||${ck}`] || 0), 0)
              const onCell = (extra, chips) => (e) => {
                e.stopPropagation()
                openDrillCell(extra, chips)
              }
              return (
                <div className={classes.pivotScroll}>
                  <table className={classes.pivotTable}>
                    <thead>
                      <tr>
                        <th className={classes.pivotCorner}>{rowKey}</th>
                        {colKeys.map((ck) => (
                          <th key={ck} className={classes.pivotColHead}>
                            {ck}
                          </th>
                        ))}
                        <th className={classes.pivotColHead}>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rowKeys.map((rk) => (
                        <tr key={rk}>
                          <th className={classes.pivotRowHead}>{rk}</th>
                          {colKeys.map((ck) => {
                            const v = cellMap[`${rk}||${ck}`] || 0
                            const clickable = v > 0 && drillCfg
                            return (
                              <td
                                key={ck}
                                className={
                                  clickable
                                    ? classes.pivotCellClickable
                                    : classes.pivotCell
                                }
                                style={
                                  v > 0
                                    ? {
                                        backgroundColor: `rgba(216,90,48,${(
                                          0.1 +
                                          0.55 * (v / maxCell)
                                        ).toFixed(2)})`
                                      }
                                    : undefined
                                }
                                onClick={
                                  clickable
                                    ? onCell(
                                        {
                                          [drillCfg.rowParam]: rk,
                                          [drillCfg.colParam]: ck
                                        },
                                        [rk, ck]
                                      )
                                    : undefined
                                }
                              >
                                {v > 0 ? v.toLocaleString() : '–'}
                              </td>
                            )
                          })}
                          <td
                            className={
                              drillCfg && rowTotal(rk) > 0
                                ? classes.pivotTotalClickable
                                : classes.pivotTotal
                            }
                            onClick={
                              drillCfg && rowTotal(rk) > 0
                                ? onCell({ [drillCfg.rowParam]: rk }, [rk])
                                : undefined
                            }
                          >
                            {rowTotal(rk).toLocaleString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )
            })()
          ) : isTable ? (
            <DrillTable
              rows={rows}
              columns={columns}
              allColumns={allColumns}
              filename={`${(dashboard.panel_title || dashboard.title || 'data')
                .toLowerCase()
                .replace(/[^a-z0-9]+/g, '-')
                .replace(/(^-|-$)/g, '')}.csv`}
              classes={classes}
              gridMode
            />
          ) : (
            <table className={classes.previewTable}>
              <thead>
                <tr>
                  {columns.map((c) => (
                    <th key={c}>{c}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(dashboard.all_rows ? rows : rows.slice(0, PREVIEW_ROWS)).map(
                  (row, i) => (
                    <tr
                      key={i}
                      className={
                        drillCfg?.column ? classes.drillRow : undefined
                      }
                    >
                      {columns.map((c) => (
                        <td
                          key={c}
                          onClick={
                            drillCfg?.column
                              ? (e) => {
                                  e.stopPropagation()
                                  openDrill(row, c)
                                }
                              : undefined
                          }
                        >
                          {renderCell(row, c, allColumns)}
                        </td>
                      ))}
                    </tr>
                  )
                )}
              </tbody>
            </table>
          )}
        </div>
        {data && rows.length > 0 ? (
          <Typography variant="caption" className={classes.dashletFooter}>
            {isRatio
              ? 'click to view list'
              : isPivot
              ? 'click a count to view its sessions'
              : isTable
              ? `${rows.length.toLocaleString()} row${
                  rows.length === 1 ? '' : 's'
                }`
              : isHBar
              ? drillCfg
                ? 'click a bar to view enrolments'
                : 'click to view data'
              : chartCfg
              ? 'click to view data'
              : drillCfg
              ? isKpi
                ? 'click to view list'
                : dashboard.all_rows
                ? 'click a number to view its list'
                : 'click a row to view enrolments'
              : `${rows.length} row${
                  rows.length === 1 ? '' : 's'
                } · click to expand`}
          </Typography>
        ) : null}
      </Paper>

      <Dialog open={dialogOpen} onClose={closeDialog} fullScreen>
        <DialogTitle disableTypography className={classes.dialogHeader}>
          <div className={classes.dialogHeaderMain}>
            <span className={classes.dialogHeaderTitle}>{dialogTitle}</span>
            {dialogChips.map((c) => (
              <Chip
                key={c}
                size="small"
                label={c}
                className={classes.dialogChip}
              />
            ))}
          </div>
          <div className={classes.dialogHeaderActions}>
            {dialogData ? (
              <span className={classes.dialogCount}>
                {dialogRows.length.toLocaleString()} record
                {dialogRows.length === 1 ? '' : 's'}
              </span>
            ) : null}
            <IconButton
              size="small"
              onClick={closeDialog}
              aria-label="Close"
              className={classes.dialogClose}
            >
              ✕
            </IconButton>
          </div>
        </DialogTitle>
        <DialogContent className={classes.dialogContent} dividers>
          {drillError ? (
            <Typography color="error">Could not load.</Typography>
          ) : drill && !dialogData ? (
            <div className={classes.dashletLoading}>
              <CircularProgress />
            </div>
          ) : dialogData ? (
            <DrillTable
              rows={dialogRows}
              columns={dialogColumns}
              allColumns={dialogAllColumns}
              filename={`${(dialogTitle || 'list')
                .toLowerCase()
                .replace(/[^a-z0-9]+/g, '-')
                .replace(/(^-|-$)/g, '')}.csv`}
              classes={classes}
            />
          ) : null}
        </DialogContent>
      </Dialog>
    </>
  )
}

const FilterBar = ({ slugs, draft, setDraft, options, onApply, classes }) => {
  if (isEmpty(slugs)) return null
  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }))
  return (
    <Paper className={classes.filterBar} elevation={0}>
      {slugs.map((slug) => {
        const label = FILTER_LABELS[slug] || slug
        if (slug === 'teacher_phone_number') {
          const teachers = options.teachers || []
          const selected = teachers.find((t) => t.value === draft[slug]) || null
          return (
            <Autocomplete
              key={slug}
              className={classes.teacherFilter}
              size="small"
              options={teachers}
              value={selected}
              getOptionLabel={(o) => (o ? `${o.label} (${o.value})` : '')}
              getOptionSelected={(o, v) => o.value === v.value}
              onChange={(_, o) => set(slug, o ? o.value : '')}
              renderInput={(p) => (
                <TextField {...p} label="Teacher" variant="outlined" />
              )}
              renderOption={(o) => (
                <div className={classes.teacherOption}>
                  <div className={classes.teacherName}>
                    {o.label}{' '}
                    <span className={classes.teacherNum}>{o.value}</span>
                  </div>
                  <div className={classes.pills}>
                    {(o.subjects || []).map((s) => (
                      <Chip
                        key={`s-${s}`}
                        size="small"
                        label={s}
                        className={classes.subjectPill}
                      />
                    ))}
                    {(o.centres || []).map((c) => (
                      <Chip
                        key={`c-${c}`}
                        size="small"
                        label={c}
                        className={classes.centrePill}
                      />
                    ))}
                  </div>
                </div>
              )}
            />
          )
        }
        if (
          slug === 'centre' ||
          slug === 'subject' ||
          slug === 'cancellation_reason' ||
          slug === 'at_home_apartment'
        ) {
          const opts =
            slug === 'centre'
              ? options.centres
              : slug === 'subject'
              ? options.subjects
              : slug === 'at_home_apartment'
              ? options.at_home_apartments
              : options.reasons
          return (
            <FormControl
              key={slug}
              size="small"
              className={
                slug === 'cancellation_reason'
                  ? classes.reasonFilter
                  : classes.filterControl
              }
            >
              <InputLabel>{label}</InputLabel>
              <Select
                value={draft[slug] || ''}
                onChange={(e) => set(slug, e.target.value)}
                label={label}
              >
                <MenuItem value="">
                  <em>All</em>
                </MenuItem>
                {(opts || []).map((o) => (
                  <MenuItem key={o} value={o}>
                    {o}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )
        }
        const type = slug.includes('date')
          ? 'date'
          : slug === 'age'
          ? 'number'
          : 'text'
        return (
          <TextField
            key={slug}
            className={classes.filterControl}
            size="small"
            type={type}
            label={label}
            value={draft[slug] || ''}
            onChange={(e) => set(slug, e.target.value)}
            InputLabelProps={type === 'date' ? { shrink: true } : undefined}
          />
        )
      })}
      <Button
        variant="contained"
        color="primary"
        onClick={onApply}
        className={classes.applyBtn}
      >
        Apply
      </Button>
    </Paper>
  )
}

// `group` mounts one dashboard group outside the Analytics menu (At Home →
// Dashboard); without it the group comes from the URL as before.
const Analytics = ({ group: fixedGroup }) => {
  const classes = useStyles()
  const { pathname, search } = useLocation()
  const groupKey = fixedGroup || groupKeyFromPath(pathname)

  const [payload, setPayload] = React.useState(null)
  const [options, setOptions] = React.useState({ centres: [], subjects: [] })
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState('')
  const [activeTab, setActiveTab] = React.useState('')
  const [draft, setDraft] = React.useState({})
  const [applied, setApplied] = React.useState({})

  React.useEffect(() => {
    let mounted = true
    Promise.all([
      listAnalyticsDashboards(),
      getAnalyticsFilterOptions().catch(() => ({ centres: [], subjects: [] }))
    ])
      .then(([list, opts]) => {
        if (!mounted) return
        setPayload(list)
        setOptions(opts || {})
      })
      .catch(
        (e) => mounted && setError(e.message || 'Failed to load analytics')
      )
      .finally(() => mounted && setLoading(false))
    return () => {
      mounted = false
    }
  }, [])

  // Reset filters/tab when switching dashboards via the sidebar.
  // ?tab=<label> opens that tab (the old Teacher Scorecard link lands on
  // Preschool Quality & Ops this way).
  React.useEffect(() => {
    setDraft({})
    setApplied({})
    const asked = new URLSearchParams(search).get('tab') || ''
    const renamed = Object.values(PAGE_TABS)
      .flat()
      .find((p) => (p.aliases || []).includes(asked))
    setActiveTab(renamed ? renamed.label : asked)
  }, [groupKey])

  const dashboards = payload?.dashboards || []
  const filterSets = payload?.filter_sets || {}

  const group = React.useMemo(() => {
    // Hidden dashboards back drill-down detail lists; never show them as cards.
    const visibleDashboards = dashboards.filter((d) => !d.hidden)
    const all = visibleDashboards.filter((d) => d.group === groupKey)
    return all.length
      ? all
      : visibleDashboards.filter((d) => d.group === visibleDashboards[0]?.group)
  }, [dashboards, groupKey])

  const tabs = React.useMemo(() => {
    const t = []
    for (const d of group) if (d.tab && !t.includes(d.tab)) t.push(d.tab)
    for (const p of PAGE_TABS[group[0]?.group] || []) t.push(p.label)
    return t
  }, [group])

  const currentTab = (tabs.includes(activeTab) && activeTab) || tabs[0] || ''
  const pageTab = (PAGE_TABS[group[0]?.group] || []).find(
    (p) => p.label === currentTab
  )
  const visible = tabs.length
    ? group.filter((d) => d.tab === currentTab)
    : group
  // A tab can extend the group's filters via a "group:Tab" filter set (e.g.
  // After School Ops adds a Cancellation Reason filter); fall back to the
  // group set.
  const slugs =
    filterSets[`${groupKey}:${currentTab}`] || filterSets[groupKey] || []
  // One line under the filters saying what an empty date range shows.
  const filterNotes = payload?.filter_notes || {}
  const filterNote =
    filterNotes[`${groupKey}:${currentTab}`] || filterNotes[groupKey] || ''

  // Some Teacher cards use {{Teacher}} instead of {{teacher_phone_number}};
  // send the same phone under both keys.
  const dashletParams = React.useMemo(
    () =>
      applied.teacher_phone_number
        ? { ...applied, teacher: applied.teacher_phone_number }
        : applied,
    [applied]
  )
  const paramsKey = JSON.stringify(dashletParams)
  const limit = React.useMemo(
    () => makeLimiter(MAX_CONCURRENT),
    [group[0]?.group, currentTab, paramsKey]
  )

  if (loading && !payload) {
    return (
      <div className={classes.centered}>
        <CircularProgress />
      </div>
    )
  }
  if (error || isEmpty(group)) {
    return (
      <Typography
        className={classes.container}
        color={error ? 'error' : 'textSecondary'}
      >
        {error || 'No dashboards configured.'}
      </Typography>
    )
  }

  const tabsBar = tabs.length ? (
    <PageTabsBar>
      <Tabs
        value={currentTab}
        onChange={(_, v) => setActiveTab(v)}
        indicatorColor="primary"
        textColor="primary"
        variant="scrollable"
      >
        {tabs.map((t) => (
          // As typed, not ALL CAPS (Satya, 28 Oct).
          <Tab key={t} value={t} label={t} style={{ textTransform: 'none' }} />
        ))}
      </Tabs>
    </PageTabsBar>
  ) : null

  if (pageTab) {
    return (
      <>
        {tabsBar}
        <pageTab.Component />
      </>
    )
  }

  return (
    <>
      {tabsBar}

      <div className={classes.container}>
        {/* The group title is redundant when a tab bar is already shown above
            it (the tabs make the section obvious), so only render it for
            single-view dashboards with no tabs. */}
        {tabs.length ? null : (
          <Typography variant="h6" className={classes.pageTitle}>
            {group[0]?.group_title}
          </Typography>
        )}

        <FilterBar
          slugs={slugs}
          draft={draft}
          setDraft={setDraft}
          options={options}
          onApply={() => setApplied({ ...draft })}
          classes={classes}
        />
        {filterNote ? (
          <Typography variant="caption" className={classes.filterNote}>
            {filterNote}
          </Typography>
        ) : null}

        {(() => {
          // When a tab's dashboards carry a `section` (e.g. Preschool /
          // Afterschool), split it into one column per section instead of the
          // flat row-by-row grid.
          const sections = []
          visible.forEach((d) => {
            if (d.section && !sections.includes(d.section)) {
              sections.push(d.section)
            }
          })
          if (sections.length) {
            const colSpan = Math.max(1, Math.floor(12 / sections.length))
            return (
              <Grid container spacing={2} key={`${currentTab}|${paramsKey}`}>
                {sections.map((sec) => (
                  <Grid item xs={12} md={colSpan} key={sec}>
                    <Typography
                      variant="subtitle1"
                      className={classes.sectionTitle}
                    >
                      {sec}
                    </Typography>
                    {visible
                      .filter((d) => d.section === sec)
                      .map((d) => (
                        <div key={d.id} className={classes.sectionItem}>
                          <Dashlet
                            dashboard={d}
                            params={dashletParams}
                            limit={limit}
                            classes={classes}
                          />
                        </div>
                      ))}
                  </Grid>
                ))}
              </Grid>
            )
          }
          return (
            <Grid container spacing={2} key={`${currentTab}|${paramsKey}`}>
              {visible.map((d) => {
                const span = gridSpan(d)
                return (
                  <Grid item xs={12} sm={span.sm} lg={span.lg} key={d.id}>
                    <Dashlet
                      dashboard={d}
                      params={dashletParams}
                      limit={limit}
                      classes={classes}
                    />
                  </Grid>
                )
              })}
            </Grid>
          )
        })()}
      </div>
    </>
  )
}

export default Analytics
